import React, { useLayoutEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  fetchSubscriptionStatus,
  selectMigration,
  selectPending,
  selectTrial,
} from "../app/features/subscriptionStatusSlice"
import { formatDate } from "../utils/dates"
import {
  PENDING_UI_HIDDEN_PATHS,
  graceMessage,
  openPendingPayment,
  pendingActionLabel,
  pendingMessage,
} from "./pendingPayment"

// Content screens render the fixed 60 px Nav (z-index 100) over the top of
// the page: there the banner sits fixed right below it, and a spacer of its
// measured height pushes the page down so nothing hides under it (D72).
const NAV_HEIGHT_PX = 60
const NAV_ROUTE_PREFIXES = ["/profile", "/search/", "/episodes/", "/series/", "/schedule"]
const rendersNav = (pathname) =>
  pathname === "/" || NAV_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))

const buttonStyle = {
  background: "#F2E1D1",
  color: "#2D2D23",
  border: "none",
  borderRadius: "4px",
  padding: "6px 14px",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: "13px",
  whiteSpace: "nowrap",
}

const outlineButtonStyle = {
  background: "transparent",
  color: "#F2E1D1",
  border: "1px solid #F2E1D1",
  borderRadius: "4px",
  padding: "6px 14px",
  cursor: "pointer",
  fontSize: "13px",
  whiteSpace: "nowrap",
}

// Height of the banner as rendered (it wraps on narrow screens), followed
// with ResizeObserver; without it (old browsers, tests) read once.
function useHeight(ref, active) {
  const [height, setHeight] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!active || !el) {
      setHeight(0)
      return
    }
    const read = () => setHeight(el.getBoundingClientRect().height)
    read()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(read)
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, active])
  return height
}

/**
 * Persistent banner at the top of the authenticated layout: a pending payment
 * (red when fewer than 3 grace days remain) or, without one, the boleto
 * migration notice. Hidden where the same state is already the main content.
 */
export default function PendingPaymentBanner() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const pending = useSelector(selectPending)
  const migration = useSelector(selectMigration)
  const trial = useSelector(selectTrial)
  const bannerRef = useRef(null)

  const visible = !PENDING_UI_HIDDEN_PATHS.includes(location.pathname) && Boolean(pending || migration)
  const fixed = visible && rendersNav(location.pathname)
  const height = useHeight(bannerRef, fixed)

  if (!visible) return null

  const urgent =
    pending && pending.graceDaysRemaining !== null && pending.graceDaysRemaining < 3
  const placement = fixed
    ? { position: "fixed", top: NAV_HEIGHT_PX, left: 0, right: 0, zIndex: 99 }
    : { position: "relative", zIndex: 1 }

  return (
    <>
      {fixed && <div aria-hidden="true" data-testid="pending-banner-spacer" style={{ height }} />}
      <div
        ref={bannerRef}
        role="alert"
        style={{
          ...placement,
          width: "100%",
          padding: "10px 16px",
          boxSizing: "border-box",
          background: urgent ? "#B33A3A" : "#C79A3A",
          color: "#F2E1D1",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          fontFamily: "'InterTight', sans-serif",
          fontSize: "14px",
        }}
      >
        <span style={{ flex: "1 1 240px", minWidth: 0 }}>
          {pending ? (
            <>
              {pendingMessage(pending, trial)}
              {pending.graceDaysRemaining > 0 && <> {graceMessage(pending.graceDaysRemaining)}</>}
            </>
          ) : (
            <>
              O boleto não é mais aceito. Para continuar depois de{" "}
              <strong>{formatDate(migration.deadline)}</strong>, escolha Pix ou cartão.
            </>
          )}
        </span>
        <span
          style={{
            display: "flex",
            gap: "8px",
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          {pending ? (
            <>
              <button
                type="button"
                onClick={() => openPendingPayment(pending, navigate)}
                style={buttonStyle}
              >
                {pendingActionLabel(pending.kind)}
              </button>
              <button
                type="button"
                onClick={() => dispatch(fetchSubscriptionStatus())}
                style={outlineButtonStyle}
              >
                Já paguei
              </button>
            </>
          ) : (
            <button type="button" onClick={() => navigate("/subscription")} style={buttonStyle}>
              Escolher agora
            </button>
          )}
        </span>
      </div>
    </>
  )
}
